import type { LoadedFont } from '../fonts/fontLoader';
import { clamp, type Rect } from '../math';
import { createRng, type Rng } from '../rng';
import type { Contour } from './flatten';
import { allContours, layoutText, type GlyphInstance, type TextLayout } from './layout';
import { rasteriseContours, type Raster } from './rasterize';
import { computeSdf } from './sdf';
import {
  sampleInterior,
  sampleOutline,
  type InteriorSampleMode,
  type OutlineSample,
} from './sampling';
import type { TextSettings } from './settings';

/**
 * The one interface every technique uses to ask questions about the text.
 *
 * Techniques never touch fonts, layout or rasterisation directly — they see a field:
 * a mask, a signed distance function, a gradient, contours and samplers. That keeps
 * each technique independent of typography concerns, and means the whole field is
 * rebuilt only when text geometry changes, not once per frame.
 *
 * Coordinates are canvas pixels throughout, y pointing down.
 */
export interface TextField {
  readonly width: number;
  readonly height: number;

  /** The layout this field was built from — per-glyph access lives here. */
  readonly layout: TextLayout;
  readonly glyphs: readonly GlyphInstance[];
  /** Flattened outlines of every glyph, in canvas space. */
  readonly contours: readonly Contour[];
  /** Tight bounding box of the drawn text. */
  readonly bounds: Rect;
  /** True when the text produced no drawable geometry (empty string, spaces only). */
  readonly isEmpty: boolean;

  /** Antialiased coverage of the *drawn* glyphs, 0–255, row-major. Not dilated. */
  readonly alpha: Uint8Array;
  /** Signed distance in px, negative inside. Includes the dilate/erode offset. */
  readonly sdf: Float32Array;

  /** Bilinear coverage of the drawn glyphs in [0, 1]. */
  coverage(x: number, y: number): number;
  /** Signed distance with bilinear interpolation; negative inside the (dilated) field. */
  distance(x: number, y: number): number;
  /** True inside the (dilated) field. */
  isInside(x: number, y: number): boolean;
  /** Unit vector pointing away from the text. Zero-length only in perfectly flat regions. */
  gradient(x: number, y: number): readonly [number, number];

  /** `n` points spaced evenly along the outline, with tangent and outward normal. */
  sampleOutline(n: number, offset?: number): OutlineSample[];
  /** Up to `n` points inside the glyphs, as a flat [x, y, ...] array. */
  samplePoints(n: number, mode?: InteriorSampleMode, rng?: Rng): Float32Array;

  /** Per-glyph coverage mask, computed on demand and cached. */
  glyphMask(glyphIndex: number): GlyphMask | null;

  /** Largest distances found, handy for normalising visualisations. */
  readonly maxOutside: number;
  readonly maxInside: number;
}

export interface GlyphMask {
  /** Position and size of the mask within the canvas. */
  rect: Rect;
  width: number;
  height: number;
  alpha: Uint8Array;
}

export interface BuildTextFieldOptions {
  settings: TextSettings;
  font: LoadedFont;
  width: number;
  height: number;
  /** Rasterisation supersampling for the mask. 1 is enough above ~40 px type. */
  supersample?: number;
}

export interface TextFieldStats {
  layoutMs: number;
  rasterMs: number;
  sdfMs: number;
  totalMs: number;
  vertices: number;
  contours: number;
}

export interface BuiltTextField {
  field: TextField;
  stats: TextFieldStats;
}

const now = (): number => (typeof performance !== 'undefined' ? performance.now() : Date.now());

export function buildTextField(opts: BuildTextFieldOptions): BuiltTextField {
  const { settings, font, width, height } = opts;
  const t0 = now();

  const layout = layoutText(settings, font, width, height);
  const contours = allContours(layout);
  const t1 = now();

  const raster: Raster = layout.isEmpty
    ? { alpha: new Uint8Array(width * height), width, height }
    : rasteriseContours(contours, width, height, opts.supersample ?? 1);
  const t2 = now();

  const { sdf, maxOutside, maxInside } = computeSdf(raster.alpha, width, height);
  // Dilate/erode is a pure offset of the field: no morphological pass needed, and it
  // stays exact at sub-pixel amounts. The drawn glyphs are left untouched on purpose.
  if (settings.dilate !== 0) {
    const d = settings.dilate;
    for (let i = 0; i < sdf.length; i++) sdf[i] -= d;
  }
  const t3 = now();

  let vertices = 0;
  for (const c of contours) vertices += c.points.length / 2;

  const field = makeField({
    width,
    height,
    layout,
    contours,
    alpha: raster.alpha,
    sdf,
    maxOutside: maxOutside - settings.dilate,
    maxInside: maxInside + settings.dilate,
  });

  return {
    field,
    stats: {
      layoutMs: t1 - t0,
      rasterMs: t2 - t1,
      sdfMs: t3 - t2,
      totalMs: t3 - t0,
      vertices,
      contours: contours.length,
    },
  };
}

interface FieldParts {
  width: number;
  height: number;
  layout: TextLayout;
  contours: Contour[];
  alpha: Uint8Array;
  sdf: Float32Array;
  maxOutside: number;
  maxInside: number;
}

function makeField(parts: FieldParts): TextField {
  const { width, height, layout, contours, alpha, sdf } = parts;
  const maxX = width - 1;
  const maxY = height - 1;
  const glyphMaskCache = new Map<number, GlyphMask | null>();
  const gradientOut: [number, number] = [0, 0];

  // Two separate bilinear readers, one per array type.
  //
  // This looks like duplication and is not: a single reader taking
  // `Float32Array | Uint8Array` becomes polymorphic at its element access and the JIT
  // deoptimises it. Measured on the flow field, that cost 172 ns per `distance()` call
  // — and `gradient()` makes four of them, so a single particle step spent most of its
  // time here. Monomorphic readers bring it down by roughly five times.

  const bilinearSdf = (x: number, y: number): number => {
    const fx = x - 0.5 < 0 ? 0 : x - 0.5 > maxX ? maxX : x - 0.5;
    const fy = y - 0.5 < 0 ? 0 : y - 0.5 > maxY ? maxY : y - 0.5;
    const x0 = fx | 0;
    const y0 = fy | 0;
    const x1 = x0 < maxX ? x0 + 1 : x0;
    const y1 = y0 < maxY ? y0 + 1 : y0;
    const tx = fx - x0;
    const ty = fy - y0;
    const r0 = y0 * width;
    const r1 = y1 * width;
    const a = sdf[r0 + x0];
    const b = sdf[r0 + x1];
    const c = sdf[r1 + x0];
    const d = sdf[r1 + x1];
    return (a + (b - a) * tx) * (1 - ty) + (c + (d - c) * tx) * ty;
  };

  const bilinearAlpha = (x: number, y: number): number => {
    const fx = x - 0.5 < 0 ? 0 : x - 0.5 > maxX ? maxX : x - 0.5;
    const fy = y - 0.5 < 0 ? 0 : y - 0.5 > maxY ? maxY : y - 0.5;
    const x0 = fx | 0;
    const y0 = fy | 0;
    const x1 = x0 < maxX ? x0 + 1 : x0;
    const y1 = y0 < maxY ? y0 + 1 : y0;
    const tx = fx - x0;
    const ty = fy - y0;
    const r0 = y0 * width;
    const r1 = y1 * width;
    const a = alpha[r0 + x0];
    const b = alpha[r0 + x1];
    const c = alpha[r1 + x0];
    const d = alpha[r1 + x1];
    return (a + (b - a) * tx) * (1 - ty) + (c + (d - c) * tx) * ty;
  };

  const distance = (x: number, y: number): number => {
    // Outside the canvas the field would be undefined; extend it linearly from the
    // border so particles that wander off do not snap back into the text.
    if (x < 0 || y < 0 || x > maxX || y > maxY) {
      const cx = clamp(x, 0, maxX);
      const cy = clamp(y, 0, maxY);
      return bilinearSdf(cx, cy) + Math.hypot(x - cx, y - cy);
    }
    return bilinearSdf(x, y);
  };

  const field: TextField = {
    width,
    height,
    layout,
    glyphs: layout.glyphs,
    contours,
    bounds: layout.bounds,
    isEmpty: layout.isEmpty,
    alpha,
    sdf,
    maxOutside: parts.maxOutside,
    maxInside: parts.maxInside,

    coverage: (x, y) => (x < 0 || y < 0 || x > maxX || y > maxY ? 0 : bilinearAlpha(x, y) / 255),
    distance,
    isInside: (x, y) => distance(x, y) < 0,

    gradient(x, y) {
      // Central differences on the interpolated field. A one-pixel step is the right
      // scale: the sub-pixel refinement in the SDF makes it smooth at that distance.
      const dx = distance(x + 1, y) - distance(x - 1, y);
      const dy = distance(x, y + 1) - distance(x, y - 1);
      const len = Math.hypot(dx, dy);
      if (len < 1e-9) {
        gradientOut[0] = 0;
        gradientOut[1] = 0;
      } else {
        gradientOut[0] = dx / len;
        gradientOut[1] = dy / len;
      }
      return gradientOut;
    },

    sampleOutline(n, offset = 0) {
      return sampleOutline(contours, n, (x, y) => field.gradient(x, y), offset);
    },

    samplePoints(n, mode: InteriorSampleMode = 'uniform', rng = createRng(1)) {
      return sampleInterior(n, {
        mode,
        bounds: layout.bounds,
        isInside: field.isInside,
        distance: field.distance,
        rng,
      });
    },

    glyphMask(glyphIndex) {
      const cached = glyphMaskCache.get(glyphIndex);
      if (cached !== undefined) return cached;

      const glyph = layout.glyphs.find((g) => g.index === glyphIndex);
      let result: GlyphMask | null = null;
      if (glyph && glyph.contours.length && glyph.bounds.width > 0 && glyph.bounds.height > 0) {
        const pad = 2;
        const rx = Math.floor(glyph.bounds.x) - pad;
        const ry = Math.floor(glyph.bounds.y) - pad;
        const rw = Math.ceil(glyph.bounds.width) + pad * 2;
        const rh = Math.ceil(glyph.bounds.height) + pad * 2;
        // Rasterise into a local buffer by shifting the contours to the mask origin.
        const shifted: Contour[] = glyph.contours.map((c) => {
          const pts = new Float32Array(c.points.length);
          for (let i = 0; i < pts.length; i += 2) {
            pts[i] = c.points[i] - rx;
            pts[i + 1] = c.points[i + 1] - ry;
          }
          return { ...c, points: pts };
        });
        const raster = rasteriseContours(shifted, rw, rh, 1);
        result = { rect: { x: rx, y: ry, width: rw, height: rh }, width: rw, height: rh, alpha: raster.alpha };
      }

      glyphMaskCache.set(glyphIndex, result);
      return result;
    },
  };

  return field;
}

export type { OutlineSample, InteriorSampleMode };
